import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { useCountryData } from '../hooks/useCountryData';
import { useScenario } from '../contexts/ScenarioContext';
import { getCountrySeries, INDICATORS, SeriesResult } from '../utils/api';
import { agentsPerCapitaFor, countryMachines, ENDPOINT_FORMULA, SCENARIO_AGENT_FORMULA } from '../utils/estimates';
import { formatCompact, formatInt, formatPct } from '../utils/format';
import { HUMANS, scenarios } from '../data/populations';
import { DataMode } from '../types/census';
import { PageLoader } from '../components/PageLoader';
import { SourceRef } from '../components/SourceRef';
import { DataBadge } from '../components/DataBadge';
import { LiveCounter } from '../components/LiveCounter';
import { TrendChart } from '../components/countries/TrendChart';
import { TopNetworks } from '../components/countries/TopNetworks';
import { AspopResult, getAspop } from '../utils/apnic';
import { speedIndex, SPEED_AS_OF } from '../data/speedIndex';
export function CountryDetail() {
  const {
    code = ''
  } = useParams();
  const cca3 = code.toUpperCase();
  const data = useCountryData();
  const {
    scenario,
    scenarioId,
    setScenarioId
  } = useScenario();
  const [trend, setTrend] = useState<SeriesResult | null>(null);
  const [sex, setSex] = useState<{
    male: SeriesResult;
    female: SeriesResult;
  } | null>(null);
  const [aspop, setAspop] = useState<AspopResult | null>(null);
  const cca2 = data.rows.find(r => r.cca3 === cca3)?.cca2 ?? null;
  useEffect(() => {
    if (!cca2) return;
    let alive = true;
    setAspop(null);
    getAspop(cca2).then(r => alive && setAspop(r));
    return () => {
      alive = false;
    };
  }, [cca2]);
  useEffect(() => {
    let alive = true;
    setTrend(null);
    setSex(null);
    getCountrySeries(cca3, INDICATORS.internet).then(t => alive && setTrend(t));
    Promise.all([getCountrySeries(cca3, INDICATORS.male), getCountrySeries(cca3, INDICATORS.female)]).then(([male, female]) => alive && setSex({
      male,
      female
    }));
    return () => {
      alive = false;
    };
  }, [cca3]);
  if (data.loading) return <PageLoader label="Loading country record" data-id="element-541" data-mp="34hllmp0eh71u" />;
  const row = data.rows.find(r => r.cca3 === cca3);
  if (!row) {
    return <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-8" data-id="element-542" data-mp="39feszldfw73p">
        <h1 className="font-serif text-3xl text-ink" data-id="element-543" data-mp="2ujfjq7elg0wz">No record for “{code}”</h1>
        <p className="mt-3 text-muted" data-id="element-544" data-mp="2ulijhj5jt8ew">Country pages use ISO 3166 alpha-3 codes, e.g. /countries/USA.</p>
        <Link to="/countries" className="mt-6 inline-block text-machine underline underline-offset-4" data-id="element-545" data-mp="2lihfbo08trdv">
          Back to all countries
        </Link>
      </div>;
  }
  const worldShare = row.population / HUMANS.base;
  const machines = countryMachines(row, scenarioId);
  const machineRate = machines != null ? scenario.ratePerSec * (machines / scenario.count) : 0;
  const male = sex?.male.points.at(-1);
  const female = sex?.female.points.at(-1);
  const malePct = male && female ? male.value / (male.value + female.value) * 100 : null;
  const speed = speedIndex[row.cca3];
  const stats: Array<{
    label: string;
    value: string;
    sub: string;
    sourceId: string;
    estimate?: boolean;
    snapshot?: boolean;
  }> = [{
    label: 'Population',
    value: formatInt(row.population),
    sub: `${formatPct(worldShare * 100, 2)} of humanity`,
    sourceId: 'restcountries'
  }, {
    label: 'Internet users',
    value: row.internetPct ? formatPct(row.internetPct.value) : '—',
    sub: row.internetUsers != null ? `${formatCompact(row.internetUsers)} people · ${row.internetPct?.year}` : 'Not reported',
    sourceId: 'wbInternet'
  }, {
    label: 'Mobile subscriptions',
    value: row.mobilePer100 ? `${row.mobilePer100.value.toFixed(1)} / 100` : '—',
    sub: row.mobilePer100 ? `World Bank ${row.mobilePer100.year}` : 'Not reported',
    sourceId: 'wbMobile'
  }, {
    label: 'Median age',
    value: row.medianAge != null ? `${row.medianAge.toFixed(1)} yrs` : '—',
    sub: 'UN WPP 2024',
    sourceId: 'unwpp'
  }, {
    label: 'Working-age share',
    value: row.workingAgePct ? formatPct(row.workingAgePct.value) : '—',
    sub: 'Ages 15–64',
    sourceId: 'wbWorking'
  }, {
    label: 'Internet speed (fixed / mobile)',
    value: speed ? `${speed.fixed} / ${speed.mobile} Mbps` : '—',
    sub: speed ? 'Median download · Ookla Speedtest Global Index' : 'Not in the Ookla top-100 snapshot — no estimate made',
    sourceId: 'ookla',
    snapshot: true
  }, {
    label: 'Sex split (male / female)',
    value: malePct != null ? `${malePct.toFixed(1)} / ${(100 - malePct).toFixed(1)}` : sex ? '—' : '…',
    sub: male ? `World Bank ${male.year}` : 'Not reported',
    sourceId: 'wbSex'
  }, {
    label: 'Assistant endpoints',
    value: row.endpoints != null ? formatCompact(row.endpoints) : '—',
    sub: ENDPOINT_FORMULA,
    sourceId: 'censusModel',
    estimate: true
  }, {
    label: `Agents per capita · ${scenario.label}`,
    value: (() => {
      const pc = agentsPerCapitaFor(row, scenarioId);
      return pc != null ? pc < 0.1 ? pc.toFixed(4) : pc.toFixed(2) : '—';
    })(),
    sub: SCENARIO_AGENT_FORMULA[scenarioId],
    sourceId: 'censusModel',
    estimate: true
  }];
  const quality: Array<{
    field: string;
    mode: DataMode | 'derived';
    detail: string;
  }> = [{
    field: 'Population, flag, capital',
    mode: data.modes.countries,
    detail: data.asOf.countries
  }, {
    field: 'Internet users',
    mode: data.modes.internet,
    detail: data.asOf.internet
  }, {
    field: 'Mobile subscriptions',
    mode: data.modes.mobile,
    detail: data.asOf.mobile
  }, {
    field: 'Working-age share',
    mode: data.modes.working,
    detail: data.asOf.working
  }, {
    field: 'Internet trend',
    mode: trend?.mode ?? 'snapshot',
    detail: trend?.asOf ?? 'loading'
  }, {
    field: 'Median age',
    mode: 'snapshot',
    detail: 'Embedded UN WPP 2024'
  }, {
    field: 'Top networks',
    mode: aspop?.mode ?? 'snapshot',
    detail: aspop ? `APNIC aspop · ${aspop.asOf}` : 'APNIC aspop · loading'
  }, {
    field: 'Speed',
    mode: 'snapshot',
    detail: `Ookla Global Index · ${SPEED_AS_OF}`
  }, {
    field: 'Endpoints & agents',
    mode: 'derived',
    detail: 'Census model on the fields above'
  }];
  return <>
      <header className="border-b border-line" data-id="element-546" data-mp="ujx0zxwxtqnc">
        <div className="mx-auto max-w-[1400px] px-5 pb-10 pt-8 md:px-8" data-id="element-547" data-mp="ul855yuap0ez">
          <Link to="/countries" className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-ink" data-id="element-548" data-mp="zizwaqn48ii4">
            <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-549" data-mp="1n8kqk5opz26p" /> All countries
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-5" data-id="element-550" data-mp="1s5kp7vj8p2ns">
            <img src={row.flag} alt={`Flag of ${row.name}`} className="h-14 w-20 rounded object-cover ring-1 ring-line" data-id="element-551" data-mp="1dihqjlj6knyv" />
            <div data-id="element-552" data-mp="1ifrndm8wg1wq">
              <h1 className="font-serif text-4xl text-ink md:text-5xl" data-id="element-553" data-mp="1if84a27ij7h4">{row.name}</h1>
              <p className="mt-1 font-mono text-[12px] text-muted" data-id="element-554" data-mp="265vg2ykjuh8y">
                {row.cca3} · {row.region} · Capital {row.capital}
              </p>
            </div>
          </div>

          <div className="mt-10 grid gap-px overflow-hidden rounded-lg border border-line bg-line md:grid-cols-2" data-id="element-555" data-mp="3vmqyan9k8kxi">
            <div className="bg-surface p-6" data-id="element-556" data-mp="3qo68zivujlnu">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-human" data-id="element-557" data-mp="8aotu30sgijc">
                Humans in {row.name}
                <SourceRef id="restcountries" data-id="element-558" data-mp="48uyma9d46qk" />
              </p>
              <p className="mt-3 text-[clamp(1.8rem,3.6vw,3rem)] leading-none text-human" data-id="element-559" data-mp="3cowt6kbjytke">
                <LiveCounter base={row.population} baseTime={HUMANS.baseTime} ratePerSec={HUMANS.ratePerSec * worldShare} label={`Humans in ${row.name}`} data-id="element-560" data-mp="3cqrcxvb38fmw" />
              </p>
              <p className="mt-3 font-mono text-[11.5px] text-muted" data-id="element-561" data-mp="3lqwazeue61oe">+{(HUMANS.ratePerSec * worldShare).toFixed(3)} / sec · world rate × population share (estimate)</p>
            </div>
            <div className="bg-surface p-6" data-id="element-562" data-mp="10y2izey8w9sw">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-machine" data-id="element-563" data-mp="w0avqy8jpbv1">
                Machines — {scenario.label}
                <SourceRef id="censusModel" estimate={machines != null ? {
                low: formatCompact(machines * 0.8),
                high: formatCompact(machines * 1.25)
              } : undefined} data-id="element-564" data-mp="1g3ghem0ncmyq" />
              </p>
              <p className="mt-3 text-[clamp(1.8rem,3.6vw,3rem)] leading-none text-machine" data-id="element-565" data-mp="1p2jzbmo2uwbn">
                {machines != null ? <LiveCounter base={machines} baseTime={HUMANS.baseTime} ratePerSec={machineRate} label={`Machines in ${row.name}`} data-id="element-566" data-mp="1tutyltb318ql" /> : <span className="font-mono" data-id="element-567" data-mp="1ysdbopw1nfgy">—</span>}
              </p>
              <div role="radiogroup" aria-label="Scenario" className="mt-3 flex flex-wrap gap-1.5" data-id="element-568" data-mp="23r6r4nuw5gjr">
                {scenarios.map(s => <button key={s.id} type="button" role="radio" aria-checked={s.id === scenarioId} onClick={() => setScenarioId(s.id)} className={`whitespace-nowrap rounded-full border px-2.5 py-1 font-mono text-[11px] transition-colors duration-150 ${s.id === scenarioId ? 'border-machine text-ink' : 'border-line text-muted hover:text-ink'}`} data-id="element-569" data-mp="28ruduxc37b86">
                    {s.label}
                  </button>)}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 pt-10 md:px-8 lg:grid-cols-[minmax(0,1fr)_380px]" data-id="element-570" data-mp="3t9df2hwwr222">
        <section aria-labelledby="stats-title" data-id="element-571" data-mp="tu8bsvk5bmwu">
          <h2 id="stats-title" className="font-serif text-2xl text-ink" data-id="element-572" data-mp="trvwkq5rvqnh">
            The record
          </h2>
          <dl className="mt-5 grid border-t border-line sm:grid-cols-2" data-id="element-573" data-mp="ot2iavinvvm9">
            {stats.map((s, i) => <div key={s.label} className={`border-b border-line py-4 ${i % 2 === 1 ? 'sm:border-l sm:pl-6' : 'sm:pr-6'}`} data-id="element-574" data-mp="fu79rcs5a9fs" data-mpi={i}>
                <dt className="text-[12.5px] text-muted" data-id="element-575" data-mp="35jsu5th71uq5">
                  {s.label}
                  {s.estimate && <span className="ml-2 rounded border border-machine/40 px-1 py-px font-mono text-[10px] text-machine" data-id="element-576" data-mp="30k6ezeht24ep">estimate</span>}
                  {s.snapshot && <span className="ml-2 rounded border border-line px-1 py-px font-mono text-[10px] text-muted" data-id="element-577" data-mp="2rcz4fqi29wod">snapshot</span>}
                </dt>
                <dd className={`mt-1 font-mono text-xl tabular ${s.estimate ? 'text-machine' : 'text-ink'}`} data-id="element-578" data-mp="307n5f4glss7d">
                  {s.value}
                  <SourceRef id={s.sourceId} estimate={s.estimate ? {
                low: '−15%',
                high: '+15%'
              } : undefined} data-id="element-579" data-mp="2vfdm479zuqe2" />
                </dd>
                <dd className="mt-0.5 break-words font-mono text-[11px] text-faint" data-id="element-580" data-mp="356giwwvw4j7j">{s.sub}</dd>
              </div>)}
          </dl>

          <div className="mt-12" data-id="element-581" data-mp="3j4y6mulspvpg">
            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-582" data-mp="3j3wt8jhb4x4k">
              <h2 className="font-serif text-2xl text-ink" data-id="element-583" data-mp="3f5fjrtaezajz">
                Internet users, % of population
                <SourceRef id="wbInternet" data-id="element-584" data-mp="1ouaz8mxdfxi1" />
              </h2>
              {trend && <DataBadge mode={trend.mode} source="World Bank" asOf={trend.asOf} data-id="element-585" data-mp="1kry3mrcc9fyz" />}
            </div>
            <p className="mt-1 text-[13px] text-muted" data-id="element-586" data-mp="1anzn53gtit7b">Last five reported years.</p>
            <div className="mt-4 rounded-lg border border-line bg-surface p-4" data-id="element-587" data-mp="1en8bttx040w0">
              {trend ? <TrendChart points={trend.points} label={`${row.name} internet users`} data-id="element-588" data-mp="10xoej3m6vvu3" /> : <p className="py-10 text-center font-mono text-xs text-muted" data-id="element-589" data-mp="15vpydqrfvz6m">Fetching series…</p>}
            </div>
          </div>

          <TopNetworks cca2={row.cca2} countryName={row.name} population={row.population} result={aspop} data-id="element-590" data-mp="qxnt9c4rf5n9" />
        </section>

        <aside aria-labelledby="quality-title" className="lg:pt-12" data-id="element-591" data-mp="vzd52tzt3nce">
          <h2 id="quality-title" className="text-[15px] font-medium text-ink" data-id="element-592" data-mp="hysjjq2ibdf7">
            Data quality
          </h2>
          <p className="mt-1 text-[13px] text-muted" data-id="element-593" data-mp="mxwxsv4ag1ce">Which fields on this page are live from an API and which are embedded snapshots.</p>
          <ul className="mt-4 divide-y divide-line rounded-lg border border-line bg-surface" data-id="element-594" data-mp="2ca7fwpq0wb3x">
            {quality.map(q => <li key={q.field} className="flex items-start justify-between gap-4 px-4 py-3" data-id="element-595" data-mp="2ewzqmutped1n">
                <div className="min-w-0" data-id="element-596" data-mp="2aud1x2sbx88o">
                  <p className="text-[13px] text-ink" data-id="element-597" data-mp="2atbia9x8zdfc">{q.field}</p>
                  <p className="truncate font-mono text-[10.5px] text-faint" data-id="element-598" data-mp="25v07nwz4oyw2">{q.detail}</p>
                </div>
                <span className={`shrink-0 font-mono text-[11px] ${q.mode === 'live' ? 'text-live' : q.mode === 'derived' ? 'text-muted' : 'text-machine'}`} data-id="element-599" data-mp="20xqh2e006h6n">{q.mode}</span>
              </li>)}
          </ul>
        </aside>
      </div>
    </>;
}